import type { PickDespatchResult } from "@shared-core/data/supplier-picks-adapter";
import {
  PICK_WORDING,
  pickResultSentence,
  pickResultsSummary,
  type SupplierTone,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { StatusPill } from "../../app/components/domain/StatusPill";

const OUTCOME: Record<
  PickDespatchResult["outcome"],
  { label: string; tone: SupplierTone }
> = {
  recorded: { label: "Recorded", tone: "ok" },
  already_recorded: { label: "Already recorded", tone: "mute" },
  refused: { label: "Refused", tone: "bad" },
};

/**
 * What came of a despatch call, one line per entry: recorded, already recorded, or refused with its sentence. A bulk
 * call can partly succeed, so a refusal never hides what went through; a refused row says what to do about it.
 * `lineRefs` names each entry by the line reference the warehouse quotes to us (the pick id is internal).
 */
export function PickResults({
  results,
  lineRefs,
  onDismiss,
}: {
  results: PickDespatchResult[];
  lineRefs: Record<string, string>;
  onDismiss: () => void;
}) {
  const refusedCount = results.filter((r) => r.outcome === "refused").length;
  return (
    <Card
      role="region"
      aria-label={PICK_WORDING.resultsTitle}
      className="gap-3 p-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        <p
          role={refusedCount > 0 ? "alert" : "status"}
          className="text-sm font-semibold"
        >
          {pickResultsSummary(results)}
        </p>
        <span className="flex-1" />
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
      <ul className="grid gap-2">
        {results.map((result, index) => {
          const outcome = OUTCOME[result.outcome];
          return (
            <li
              key={`${result.pick_id}-${result.delivery_line_id ?? ""}-${index}`}
              className="flex flex-wrap items-start gap-x-3 gap-y-1 text-sm"
            >
              <span className="font-mono text-xs">
                {lineRefs[result.pick_id] ?? "Unknown pick"}
              </span>
              <StatusPill label={outcome.label} tone={outcome.tone} />
              {result.outcome === "refused" && (
                <span className="basis-full text-muted-foreground sm:basis-auto">
                  {pickResultSentence(result)}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
